import { environment } from '../config/environment'; // Assuming referral endpoints are under the same base URL
import { useApiClient } from './api'; // Assuming you have this hook
import { UpdatePromoterPayload, PromoterApiResponse, PayoutMethod, CommissionResponse } from './referral.types'; // Import correct response types

export const useReferralService = () => {
    const apiClient = useApiClient();

    /**
     * Fetches promoter data (like referral link). 
     * API returns a nested structure defined by PromoterApiResponse.
     * Assumes user is identified via auth context.
     */
    const getPromoterData = async (): Promise<PromoterApiResponse> => {
        try {
            // Expect the nested response structure from the API call
            const response = await apiClient.post<PromoterApiResponse>(`${environment.expressApiUrl}/api/referral/promoter`, {}); 
            return response.data; 
        } catch (error) {
            console.error('Error fetching promoter data:', error);
            // Let React Query handle the error state by re-throwing
            throw error;
        }
    };

    /**
     * Updates promoter's PayPal email using the new payout methods logic.
     * API returns the updated/created PayoutMethod object.
     * Assumes user is identified via auth context.
     */
    // Takes the updated payload type, returns the updated response type (PayoutMethod)
    const updatePromoterPaypal = async (payload: UpdatePromoterPayload): Promise<PayoutMethod> => {
        try {
            // Still uses PUT /api/referral/update-promoter endpoint, but expects PayoutMethod response
            const response = await apiClient.put<PayoutMethod>(`${environment.expressApiUrl}/api/referral/update-promoter`, payload); 
            return response.data;
        } catch (error) {
            console.error('Error updating promoter PayPal email:', error);
            // Let React Query handle the error state by re-throwing
            throw error;
        }
    };
    /**
     * Fetches commission data for the current user's promoter account.
     * Returns the commissions list and a summary of earnings.
     * @param promoterId Optional promoter ID to pass directly to the API
     */
    const getCommissions = async (promoterId?: number): Promise<CommissionResponse> => {
        try {
            // If promoterId is provided, pass it as a query parameter
            const params = promoterId ? { promoterId } : {};
            const response = await apiClient.get<CommissionResponse>(
                `${environment.expressApiUrl}/api/referral/commissions`, 
                { params }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching commission data:', error);
            // Let React Query handle the error state by re-throwing
            throw error;
        }
    };

    return {
        getPromoterData,
        updatePromoterPaypal,
        getCommissions, 
    };
};
